import { useEffect, useRef, useState } from "react";
import { formatDuration } from "../lib/time";

type Props = {
  /** When the turn started (ms epoch). Set the moment the prompt is sent. */
  startedAt: number;
  /** While true the timer ticks up once a second; when it flips false the
   *  value freezes at the elapsed total so it reads like Claude Code's "done in
   *  Xs" rather than resetting or drifting. */
  streaming: boolean;
};

/**
 * Live "thinking" timer — counts up while ThunderAy AI is working on a turn,
 * then freezes on the final elapsed time. Timing is frontend-local (the same
 * `Date.now()` basis the progress trail already uses), so it needs no backend
 * support. While streaming the elapsed value is read live from `Date.now()` on
 * each render and a one-second interval forces those renders; once streaming
 * stops the end time is captured once and held.
 */
export default function ElapsedTimer({ startedAt, streaming }: Props) {
  const [, forceTick] = useState(0);
  // The instant streaming stopped, captured once so the frozen value does not
  // keep growing on later re-renders of a settled turn.
  const frozenEndRef = useRef<number | null>(null);

  useEffect(() => {
    if (!streaming) {
      // Streaming stopped: capture the end time once. Done in the effect rather
      // than during render so there is no render-time side effect (which would
      // also double-fire under React StrictMode).
      if (frozenEndRef.current === null) frozenEndRef.current = Date.now();
      return;
    }
    // (Re)started streaming: drop any prior freeze and re-render every second.
    frozenEndRef.current = null;
    const id = setInterval(() => forceTick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, [streaming]);

  const endMs = streaming ? Date.now() : (frozenEndRef.current ?? Date.now());
  const label = formatDuration(endMs - startedAt);

  return (
    <span
      className={`elapsed-timer${streaming ? " ticking" : ""}`}
      aria-label={`Elapsed time ${label}`}
    >
      {label}
    </span>
  );
}
